import React, { createContext, PropsWithChildren, useCallback, useContext, useMemo, useReducer, useState } from 'react';
import {
  AnswerKey,
  AnswerLabels,
  AdapterAtcData,
  Answers,
  BikeAdapterProduct,
  FitAssistantBikesData,
  FlowOption,
  NodeId,
} from './Types/Types.ts';
import { FLOW_START_NODE, getFlowNode } from './Flow.ts';

type AppState = {
  showFitAssistant: boolean;
  setShowFitAssistant: (show: boolean) => void;
  currentNodeId: NodeId;
  selectOption: (option: FlowOption) => void;
  goBack: () => void;
  resetFlow: () => void;
  canGoBack: boolean;
  answers: Answers;
  selection: string;
  products: BikeAdapterProduct[];
  setProducts: (products: BikeAdapterProduct[]) => void;
  fitAssistantBikesData: FitAssistantBikesData | null;
  adapterAtcDataByProductId: Record<string, AdapterAtcData>;
  setAdapterAtcDataForProductId: (productId: string, atcData: AdapterAtcData) => void;
};

const AppStateContext = createContext({} as AppState);

type FlowState = {
  currentNodeId: NodeId;
  history: NodeId[];
  answers: Answers;
  answerLabels: AnswerLabels;
  products: BikeAdapterProduct[];
  adapterAtcDataByProductId: Record<string, AdapterAtcData>;
};

type FlowAction =
  | { type: 'select'; option: FlowOption }
  | { type: 'back' }
  | { type: 'reset' }
  | { type: 'setProducts'; products: BikeAdapterProduct[] }
  | { type: 'setAdapterAtcData'; productId: string; atcData: AdapterAtcData };

const initialFlowState: FlowState = {
  currentNodeId: FLOW_START_NODE,
  history: [],
  answers: {},
  answerLabels: {},
  products: [],
  adapterAtcDataByProductId: {},
};

const clearFutureAnswers = (state: FlowState, keysToClear: AnswerKey[]): FlowState => {
  if (!keysToClear.length) {
    return state;
  }
  const answers = { ...state.answers };
  const answerLabels = { ...state.answerLabels };
  keysToClear.forEach((key) => {
    delete answers[key];
    delete answerLabels[key];
  });
  return { ...state, answers, answerLabels };
};

const reducer = (state: FlowState, action: FlowAction): FlowState => {
  if (action.type === 'setProducts') {
    return { ...state, products: action.products };
  }

  if (action.type === 'reset') {
    return { ...initialFlowState, products: state.products };
  }

  if (action.type === 'setAdapterAtcData') {
    return {
      ...state,
      adapterAtcDataByProductId: {
        ...state.adapterAtcDataByProductId,
        [action.productId]: action.atcData,
      },
    };
  }

  if (action.type === 'back') {
    if (state.history.length === 0) {
      return state;
    }
    const currentNode = getFlowNode(state.currentNodeId);
    const previousNode = state.history[state.history.length - 1];
    const previousFlowNode = getFlowNode(previousNode);
    const nextState = {
      ...state,
      currentNodeId: previousNode,
      history: state.history.slice(0, -1),
    };

    const keysToClear: AnswerKey[] = [];
    if (currentNode.answerKey) {
      keysToClear.push(currentNode.answerKey);
    }
    if (previousFlowNode.answerKey) {
      keysToClear.push(previousFlowNode.answerKey);
    }

    if (!keysToClear.length) {
      return nextState;
    }

    return clearFutureAnswers(nextState, [...new Set(keysToClear)]);
  }

  const currentNode = getFlowNode(state.currentNodeId);
  if (currentNode.kind !== 'choice' || !currentNode.answerKey || !currentNode.getNext) {
    return state;
  }

  const nextAnswers = { ...state.answers, [currentNode.answerKey]: action.option.value };
  const nextAnswerLabels = { ...state.answerLabels, [currentNode.answerKey]: action.option.label };
  const nextNode = action.option.nextNodeId
    ? action.option.nextNodeId
    : currentNode.getNext({ answers: nextAnswers, products: state.products }, action.option.value);

  return {
    ...state,
    currentNodeId: nextNode,
    history: [...state.history, state.currentNodeId],
    answers: nextAnswers,
    answerLabels: nextAnswerLabels,
  };
};

const AppStateProvider = ({ children }: PropsWithChildren) => {
  const [showFitAssistant, setShowFitAssistant] = useState(false);
  const [flowState, dispatch] = useReducer(reducer, initialFlowState);
  const [fitAssistantBikesData] = useState<FitAssistantBikesData | null>(window._THULEDATA?.FitAssistantBikes ?? null);

  const selection = useMemo(
    () =>
      Object.entries(flowState.answerLabels)
        .filter(
          ([key]) => flowState.answers[key as AnswerKey] !== 'yes' && flowState.answers[key as AnswerKey] !== 'no'
        )
        .map(([, label]) => label)
        .filter(Boolean)
        .join(' / '),
    [flowState.answerLabels, flowState.answers]
  );

  const selectOption = useCallback((option: FlowOption) => {
    dispatch({ type: 'select', option });
  }, []);

  const goBack = useCallback(() => {
    dispatch({ type: 'back' });
  }, []);

  const resetFlow = useCallback(() => {
    dispatch({ type: 'reset' });
  }, []);

  const setProducts = useCallback((products: BikeAdapterProduct[]) => {
    dispatch({ type: 'setProducts', products });
  }, []);

  const setAdapterAtcDataForProductId = useCallback((productId: string, atcData: AdapterAtcData) => {
    dispatch({ type: 'setAdapterAtcData', productId, atcData });
  }, []);

  const value: AppState = {
    showFitAssistant,
    setShowFitAssistant,
    currentNodeId: flowState.currentNodeId,
    selectOption,
    goBack,
    resetFlow,
    canGoBack: flowState.history.length > 0,
    answers: flowState.answers,
    selection,
    products: flowState.products,
    setProducts,
    fitAssistantBikesData,
    adapterAtcDataByProductId: flowState.adapterAtcDataByProductId,
    setAdapterAtcDataForProductId,
  };

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
};

const useAppState = () => {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within an AppStateProvider');
  }
  return context;
};

export { AppStateProvider, useAppState };
